import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Card } from "../../../../../components/StyledComponents";
import Button from "../../../../../components/Button";
import { Lock } from "lucide-react";
import {
  VerifyMfaCodeModal,
  BackupCodeModal,
  PersuasionModalProfile,
} from "../../../../../components/MfaModals";
import mfa from "../../../../../services/mfa";
import { toast } from "react-toastify";
import { UserDto } from "../../../../../types/user.dto";

interface Props {
  user: UserDto;
}

const MfaSection: React.FC<Props> = ({ user }) => {
  const [verifyMfaModal, setVerifyMfaModal] = useState(false);
  const [backupModal, setBackupModal] = useState(false);
  const [persuasionModal, setPersuasionModal] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);

  useEffect(() => {
    // Check if MFA is enabled (twofa === 1)
    if (user?.twofa === 1) {
      setIsMfaEnabled(true);
    } else if (user?.twofa === 0 || user?.twofa === 2) {
      // Only show MFA section if twofa is 0 (disabled) or 2 (temporarily disabled)
      // Don't show if twofa is null (not configured yet)
      // Check if we should show persuasion modal
      const lastPersuasionDate = localStorage.getItem("mfa_persuasion_date");
      if (lastPersuasionDate) {
        const lastDate = new Date(lastPersuasionDate);
        const daysSince = Math.floor(
          (Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        if (daysSince >= 30) {
          setPersuasionModal(true);
        }
      } else {
        setPersuasionModal(true);
      }
    }
  }, [user]);

  const handleVerifyMfa = async () => {
    try {
      const response = await mfa.getBackupCode();
      setBackupCode(response.code);
      setVerifyMfaModal(false); // Close verify modal before opening backup modal
      setBackupModal(true);
    } catch (error: any) {
      toast.error(error.message || "Error al obtener el código de respaldo");
    }
  };

  const handleBackupModalToggle = () => {
    setBackupModal(false);
    setVerifyMfaModal(false);
    // Delay clearing the backup code to allow modal to close first
    setTimeout(() => {
      setBackupCode("");
    }, 300);
  };

  const handleConfigureNow = () => {
    setPersuasionModal(false);
    // Redirect to MFA configuration
    window.location.href = "/dashboard/profile?mfa=setup";
  };

  if (!isMfaEnabled && user?.twofa !== null) {
    return (
      <>
        <Card style={{ marginBottom: "24px" }}>
          <div style={{ padding: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Lock size={20} color="#0066cc" />
                <h5 style={{ margin: "0 0 0 12px", fontSize: "18px" }}>
                  Autenticación de dos factores
                </h5>
              </div>
            </div>

            <p style={{ color: "#666", marginBottom: "16px" }}>
              La autenticación de dos factores agrega una capa extra de
              seguridad a tu cuenta. Actualmente no está habilitada.
            </p>

            <Button
              type="button"
              options={{ skin: "primary", size: "md", type: "filled" }}
              onClick={() => handleConfigureNow()}
            >
              Habilitar autenticación de dos factores
            </Button>
          </div>
        </Card>

        <PersuasionModalProfile
          isOpen={persuasionModal}
          toggle={() => setPersuasionModal(false)}
          onConfigure={handleConfigureNow}
        />
      </>
    );
  }

  // Don't render anything if twofa is null (not yet configured)
  if (user?.twofa === null) {
    return null;
  }

  return (
    <>
      <Card style={{ marginBottom: "24px" }}>
        <div style={{ padding: "24px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Lock size={20} color="#0066cc" />
              <h5 style={{ margin: "0 0 0 12px", fontSize: "18px" }}>
                Autenticación de dos factores
              </h5>
            </div>
            <div
              style={{
                backgroundColor: "#10b981",
                color: "white",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              HABILITADA
            </div>
          </div>

          <p style={{ color: "#666", marginBottom: "16px" }}>
            Protege tu cuenta de Legales con una capa extra de seguridad. Una
            vez configurada, tendrás que introducir tanto tu contraseña como un
            código de autenticación desde tu teléfono para iniciar sesión.
          </p>

          <Button
            type="button"
            options={{ skin: "primary", size: "md", type: "outline" }}
            onClick={() => setVerifyMfaModal(true)}
          >
            Regenerar código de respaldo
          </Button>
        </div>
      </Card>

      <VerifyMfaCodeModal
        isOpen={verifyMfaModal}
        toggle={() => setVerifyMfaModal(false)}
        handleNextModal={() => setBackupModal(true)}
        isVerified={handleVerifyMfa}
      />

      <BackupCodeModal
        isOpen={backupModal}
        toggle={handleBackupModalToggle}
        backupCode={backupCode}
      />
    </>
  );
};

const states = ({ userStore }) => {
  const { data: user } = userStore.information;
  return {
    user,
  };
};

export default connect(states)(MfaSection);
